import { useEffect, useState } from 'react';
import { get } from 'aws-amplify/api';
import { useAuth } from '@/lib/auth/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useToast } from '@/hooks/use-toast';

type Environment = 'prod' | 'uat' | 'dev';
type PlatformKey = 'ios' | 'android' | 'unknown';

const SUBSCRIBER_ENVIRONMENTS: Environment[] = ['prod', 'uat', 'dev'];

interface VersionBreakdown {
  value: string;
  platform: PlatformKey;
  count: number;
}

interface AudienceSummary {
  totalActiveAudience: number;
  uniqueInstallations: number;
  platformBreakdown: {
    ios: number;
    android: number;
    unknown: number;
  };
  deliveryModeBreakdown: {
    iosBroadcast: number;
    iosLiveActivityToken: number;
    androidFcm: number;
    unknown: number;
  };
  activeIosRegistrations: number;
  appVersions: VersionBreakdown[];
  osVersions: VersionBreakdown[];
}

interface SubscriberEvent {
  eventId: string;
  homeTeam: string;
  awayTeam: string;
  league: string;
  isComplete: boolean;
  eventStartTime: string;
  current: AudienceSummary;
}

interface SubscribersEnvironmentSnapshot {
  environment: Environment;
  generatedAt: string;
  lookbackDays: number;
  events: SubscriberEvent[];
  summary: AudienceSummary;
}

interface SubscribersResponse {
  generatedAt: string;
  lookbackDays: number;
  environments: SubscribersEnvironmentSnapshot[];
}

interface SubscriberRow extends SubscriberEvent {
  environment: Environment;
}

const emptyAudienceSummary = (): AudienceSummary => ({
  totalActiveAudience: 0,
  uniqueInstallations: 0,
  platformBreakdown: {
    ios: 0,
    android: 0,
    unknown: 0,
  },
  deliveryModeBreakdown: {
    iosBroadcast: 0,
    iosLiveActivityToken: 0,
    androidFcm: 0,
    unknown: 0,
  },
  activeIosRegistrations: 0,
  appVersions: [],
  osVersions: [],
});

const mergeVersionBreakdowns = (breakdowns: VersionBreakdown[]): VersionBreakdown[] => {
  const counts = new Map<string, VersionBreakdown>();

  for (const breakdown of breakdowns) {
    const key = `${breakdown.platform}:${breakdown.value}`;
    const existing = counts.get(key);
    if (existing) {
      existing.count += breakdown.count;
    } else {
      counts.set(key, { ...breakdown });
    }
  }

  return [...counts.values()]
    .sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }

      if (left.platform !== right.platform) {
        return left.platform.localeCompare(right.platform);
      }

      return left.value.localeCompare(right.value);
    })
    .slice(0, 5);
};

const aggregateAudienceSummaries = (summaries: AudienceSummary[]): AudienceSummary => {
  const aggregate = emptyAudienceSummary();

  for (const summary of summaries) {
    aggregate.totalActiveAudience += summary.totalActiveAudience;
    aggregate.uniqueInstallations += summary.uniqueInstallations;
    aggregate.platformBreakdown.ios += summary.platformBreakdown.ios;
    aggregate.platformBreakdown.android += summary.platformBreakdown.android;
    aggregate.platformBreakdown.unknown += summary.platformBreakdown.unknown;
    aggregate.deliveryModeBreakdown.iosBroadcast += summary.deliveryModeBreakdown.iosBroadcast;
    aggregate.deliveryModeBreakdown.iosLiveActivityToken += summary.deliveryModeBreakdown.iosLiveActivityToken;
    aggregate.deliveryModeBreakdown.androidFcm += summary.deliveryModeBreakdown.androidFcm;
    aggregate.deliveryModeBreakdown.unknown += summary.deliveryModeBreakdown.unknown;
    aggregate.activeIosRegistrations += summary.activeIosRegistrations;
    aggregate.appVersions.push(...summary.appVersions);
    aggregate.osVersions.push(...summary.osVersions);
  }

  aggregate.appVersions = mergeVersionBreakdowns(aggregate.appVersions);
  aggregate.osVersions = mergeVersionBreakdowns(aggregate.osVersions);

  return aggregate;
};

const formatVersionBreakdowns = (versions: VersionBreakdown[]) => {
  if (!versions.length) {
    return '—';
  }

  return versions
    .slice(0, 3)
    .map((version) => `${version.platform} ${version.value} (${version.count})`)
    .join(', ');
};

const formatEventStartTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

const getEventStatus = (eventStartTime: string, isComplete: boolean) => {
  if (isComplete) {
    return { label: 'Complete', className: 'bg-emerald-500/20 text-emerald-200' };
  }

  if (new Date(eventStartTime).getTime() > Date.now()) {
    return { label: 'Upcoming', className: 'bg-sky-500/20 text-sky-200' };
  }

  return { label: 'Active / Recent', className: 'bg-amber-500/20 text-amber-200' };
};

const mergeSubscribersResponses = (responses: SubscribersResponse[]): SubscribersResponse => {
  const environments = responses
    .flatMap((response) => response.environments)
    .sort(
      (left, right) =>
        SUBSCRIBER_ENVIRONMENTS.indexOf(left.environment) - SUBSCRIBER_ENVIRONMENTS.indexOf(right.environment),
    );

  const generatedAt = responses
    .map((response) => response.generatedAt)
    .sort((left, right) => right.localeCompare(left))[0] ?? new Date().toISOString();

  return {
    generatedAt,
    lookbackDays: responses[0]?.lookbackDays ?? 14,
    environments,
  };
};

export default function Subscribers() {
  const { isAuthenticated, session } = useAuth();
  const { toast } = useToast();

  const [data, setData] = useState<SubscribersResponse | null>(null);
  const [showAllEnvironments, setShowAllEnvironments] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscriberData();
    } else {
      setData(null);
    }
  }, [isAuthenticated, session?.tokens?.idToken, showAllEnvironments]);

  const fetchSubscriberData = async () => {
    if (!session?.tokens?.idToken) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const requestedEnvironments = showAllEnvironments ? SUBSCRIBER_ENVIRONMENTS : (['prod'] as Environment[]);
      const results = await Promise.allSettled(
        requestedEnvironments.map(async (environment) => {
          const response = get({
            apiName: 'dashboard',
            path: '/data/subscribers',
            options: {
              queryParams: {
                environment,
              },
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.tokens.idToken}`,
              },
            },
          });

          const { body } = await response.response;
          return (await body.json()) as unknown as SubscribersResponse;
        }),
      );

      const successfulResponses = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
      if (!successfulResponses.length) {
        throw new Error('Failed to fetch subscriber data');
      }

      const failedEnvironments = results.flatMap((result, index) =>
        result.status === 'rejected' ? [requestedEnvironments[index]] : [],
      );

      if (failedEnvironments.length > 0) {
        toast({
          title: 'Some subscriber data is unavailable',
          description: `Failed to load: ${failedEnvironments.join(', ')}`,
          variant: 'destructive',
        });
      }

      setData(mergeSubscribersResponses(successfulResponses));
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Failed to fetch subscriber data';
      setError(message);

      toast({
        title: 'Subscribers unavailable',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const visibleEnvironments = (data?.environments ?? []).filter((environment) =>
    showAllEnvironments ? true : environment.environment === 'prod',
  );

  const summary = aggregateAudienceSummaries(visibleEnvironments.map((environment) => environment.summary));
  const rows: SubscriberRow[] = visibleEnvironments
    .flatMap((environment) =>
      environment.events.map((event) => ({
        ...event,
        environment: environment.environment,
      })),
    )
    .sort((left, right) => {
      const startDifference = new Date(right.eventStartTime).getTime() - new Date(left.eventStartTime).getTime();
      if (startDifference !== 0) {
        return startDifference;
      }

      return left.environment.localeCompare(right.environment);
    });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <LoadingSpinner size="lg" message="Loading subscriber data..." />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Subscribers</h1>
          <p className="text-sm text-slate-400">
            Active NotificationCore audience for recent and upcoming matches, with platform and delivery-path breakdowns.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active Audience Rows</CardDescription>
            <CardTitle>{summary.totalActiveAudience}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-slate-400">
            Generated: {data?.generatedAt ? new Date(data.generatedAt).toLocaleString() : '—'}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Unique Installations</CardDescription>
            <CardTitle>{summary.uniqueInstallations}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-slate-400">
            Window: last {data?.lookbackDays ?? 14} days plus upcoming deployed matches
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Platform Split</CardDescription>
            <CardTitle>
              iOS {summary.platformBreakdown.ios} / Android {summary.platformBreakdown.android}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-slate-400">
            Unknown: {summary.platformBreakdown.unknown}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Delivery Paths</CardDescription>
            <CardTitle>{summary.deliveryModeBreakdown.iosBroadcast} broadcast</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-slate-400">
            Token {summary.deliveryModeBreakdown.iosLiveActivityToken} | Android {summary.deliveryModeBreakdown.androidFcm}
            <br />
            Active iOS registrations: {summary.activeIosRegistrations}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Audience Breakdown</CardTitle>
          <CardDescription>
            SNS topic counts have been removed. This view now reports directly from NotificationCore audience and registration state.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showAllEnvironments"
                checked={showAllEnvironments}
                onCheckedChange={(checked) => setShowAllEnvironments(checked === true)}
              />
              <Label htmlFor="showAllEnvironments">Show all environments</Label>
            </div>
            <div className="text-xs text-slate-400">
              Top app versions: {formatVersionBreakdowns(summary.appVersions)}
            </div>
          </div>

          {error ? (
            <div className="bg-red-50 p-4 rounded-md text-red-700">
              <p className="font-medium">Error loading subscriber data</p>
              <p className="text-sm">{error}</p>
            </div>
          ) : rows.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead>Env</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Current</TableHead>
                    <TableHead>Platforms</TableHead>
                    <TableHead>Delivery Paths</TableHead>
                    <TableHead>App Versions</TableHead>
                    <TableHead>OS Versions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={`${row.environment}-${row.eventId}`}>
                      <TableCell>{row.environment}</TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {row.homeTeam} vs {row.awayTeam}
                        </div>
                        <div className="font-mono text-[11px] text-slate-400">{row.eventId}</div>
                      </TableCell>
                      <TableCell>{formatEventStartTime(row.eventStartTime)}</TableCell>
                      <TableCell>
                        <Badge className={getEventStatus(row.eventStartTime, row.isComplete).className}>
                          {getEventStatus(row.eventStartTime, row.isComplete).label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{row.current.totalActiveAudience}</div>
                        <div className="text-[11px] text-slate-400">
                          installs {row.current.uniqueInstallations} | regs {row.current.activeIosRegistrations}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>iOS {row.current.platformBreakdown.ios}</div>
                        <div>Android {row.current.platformBreakdown.android}</div>
                        {row.current.platformBreakdown.unknown > 0 ? (
                          <div className="text-[11px] text-slate-400">Unknown {row.current.platformBreakdown.unknown}</div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <div>Broadcast {row.current.deliveryModeBreakdown.iosBroadcast}</div>
                        <div>Token {row.current.deliveryModeBreakdown.iosLiveActivityToken}</div>
                        <div>Android {row.current.deliveryModeBreakdown.androidFcm}</div>
                      </TableCell>
                      <TableCell className="max-w-[260px]">
                        <span className="text-[11px] text-slate-300">{formatVersionBreakdowns(row.current.appVersions)}</span>
                      </TableCell>
                      <TableCell className="max-w-[260px]">
                        <span className="text-[11px] text-slate-300">{formatVersionBreakdowns(row.current.osVersions)}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-muted-foreground">No subscriber data available</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
